import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Settings, FileText, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { NAV } from "../constants/testIds";

// Avatar dropdown — Profile / Settings / Terms & Conditions / Logout
export default function AvatarMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, [open]);

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        data-testid={NAV.avatar}
        aria-label="Open user menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: 36, height: 36, borderRadius: "50%",
          background: "var(--dnya-mosaic-2)",
          color: "var(--dnya-high-1)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 600, fontSize: 13, marginLeft: 8,
          border: `1px solid var(--dnya-high-1)`,
          overflow: "hidden", cursor: "pointer", padding: 0,
        }}
      >
        {user?.profile_photo_url ? (
          <img src={user.profile_photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          (user?.full_name?.[0] || "D").toUpperCase()
        )}
      </button>

      {open && (
        <div
          role="menu"
          data-testid="avatar-menu"
          style={{
            position: "absolute",
            right: 0,
            top: 44,
            minWidth: 200,
            background: "var(--dnya-card)",
            border: "0.5px solid var(--dnya-border)",
            borderRadius: 12,
            boxShadow: "0 14px 36px rgba(0,0,0,0.45)",
            overflow: "hidden",
            zIndex: 2000,
          }}
        >
          {user && (
            <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--dnya-mosaic-1)" }}>
              <div style={{ color: "var(--dnya-high-1)", fontSize: 13, fontWeight: 600 }}>{user.full_name}</div>
              <div style={{ color: "var(--dnya-text-muted)", fontSize: 11, marginTop: 2 }}>{user.email}</div>
            </div>
          )}
          <MenuItem icon={User} label="Profile" testId="avatar-menu-profile" onClick={() => go("/profile")} />
          <MenuItem icon={Settings} label="Settings" testId="avatar-menu-settings" onClick={() => go("/settings")} />
          <MenuItem icon={FileText} label="Terms & Conditions" testId="avatar-menu-terms" onClick={() => go("/terms")} />
          <div style={{ borderTop: "1px solid var(--dnya-mosaic-1)" }} />
          <MenuItem icon={LogOut} label="Logout" testId="avatar-menu-logout" onClick={() => { setOpen(false); logout(); }} />
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--dnya-mosaic-1)"; e.currentTarget.style.color = "#ffffff"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--dnya-text)"; }}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10,
        padding: "10px 14px",
        background: "transparent",
        color: "var(--dnya-text)",
        border: 0, textAlign: "left", cursor: "pointer",
        fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 500,
      }}
    >
      <Icon size={16} /> {label}
    </button>
  );
}
